import { useRequest } from 'ahooks'

import useOTPInput from '$actions/useOTPInput'
import useTOTPBlock from '$actions/useTOTPBlock'

import { asyncSuccessResolver, toast } from '$app/utilities'
import {
  errorMessageResolver,
  errorStatusResolver,
  successResolver
} from '$services/api/common'

import { FactorHookOptions } from './types'
import { useId } from 'react'

type UseAuthenticatorTOTPOptions = FactorHookOptions

export const useAuthenticatorTOTP = ({
  preferred_mfa_token_type = 'ott',
  apiInstance: { mfaChallengeAuthenticatorTOTPVerify },
  onVerified
}: UseAuthenticatorTOTPOptions) => {
  const toastID = useId()

  const onApiError = (err: unknown) => {
    if (errorStatusResolver(err) === 401) {
      resetActions()
    }
  }

  const verifyReq = useRequest(
    asyncSuccessResolver(mfaChallengeAuthenticatorTOTPVerify, successResolver),
    {
      manual: true,
      onError: onApiError,
      onSuccess: res => onVerified(res.data.mfa_token)
    }
  )

  const requests = [verifyReq]

  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)
  const disabled = isAnyLoading

  const otpInput = useOTPInput({
    disabled,
    onFilled: (code, resetInput) => {
      const { runAsync, cancel } = verifyReq

      cancel()

      toast
        .promise(runAsync({ code, type: preferred_mfa_token_type }), {
          error: errorMessageResolver,
          loading: 'Verifying..',
          success: 'Verified.',
          id: toastID
        })
        .unwrap()
        .finally(resetInput)
    }
  })

  const totpBlock = useTOTPBlock({
    otpInput,
    title: 'TOTP Verification',
    description: 'Get the code from your authenticator app.'
  })

  const resetActions = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
    otpInput.resetPasswords()
  }

  const reset = () => {
    resetActions()
    toast.dismiss(toastID)
  }

  return {
    isAnyLoading,
    reset,
    totpBlock
  }
}

export default useAuthenticatorTOTP
